import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  ViewChild,
  inject,
  input,
  signal,
} from "@angular/core";
import { prefersReducedMotion } from "@bitwarden/web-vault/app/shared/utils/reduced-motion.util";
import "./card-glare.component.scss";

/**
 * A single soft reflection sweeping across a card face — GLOSS DS 2.1 card motion spec, A2.
 * Colors are passed in as raw CSS values by the caller (the card template already knows whether
 * it's the gold or platinum variant), rather than an enum owned by this component.
 */
@Component({
  selector: "app-card-glare",
  templateUrl: "./card-glare.component.html",
})
export class CardGlareComponent implements AfterViewInit {
  // No default: `[style.--glare-core]` (see the .html) removes the custom property outright when
  // this is undefined, letting the consumer's `var(--glare-core, <fallback>)` apply. A "" default
  // would instead *set* the property to an empty value — CSS var() only falls back on a wholly
  // unset property, not an empty one, so the declaration referencing it would go invalid instead.
  glareCore = input<string>();
  glareSoft = input<string>();
  glareBlend = input<"screen" | "overlay">("screen");

  protected readonly reducedMotion = signal(false);

  @ViewChild("glareEl") private glareEl!: ElementRef<HTMLElement>;

  private readonly ngZone = inject(NgZone);

  ngAfterViewInit(): void {
    // One persistent listener (not a fresh `{ once: true }` per play()) so a rapid re-trigger
    // mid-animation can't leak listeners; it always just removes the class, however the current
    // run ends.
    this.ngZone.runOutsideAngular(() => {
      this.glareEl.nativeElement.addEventListener("animationend", () => {
        this.glareEl.nativeElement.classList.remove("is-playing");
      });
    });
  }

  play(): void {
    this.reducedMotion.set(prefersReducedMotion());
    const el = this.glareEl?.nativeElement;
    if (!el) {
      return;
    }
    el.classList.remove("is-playing");
    void el.offsetWidth; // force reflow so the removal above actually takes effect before re-adding
    el.classList.add("is-playing");
  }
}
